import type {
  QuickBooksFpaCacheManifest,
  QuickBooksFpaCachePrepareResponse,
  QuickBooksFpaCacheTablePayload,
  ServerApiClient,
} from '../../data/ServerApiClient'

export const QBO_GL_DETAIL_TABLE = 'fg_qbo_fpa_gl_detail'
export const QBO_GL_DETAIL_RAW_TABLE = `${QBO_GL_DETAIL_TABLE}_raw`
export const QBO_GL_DETAIL_ACCOUNT_TABLE = `${QBO_GL_DETAIL_TABLE}_account_lookup`
export const QBO_GL_DETAIL_ACCOUNT_RAW_TABLE = `${QBO_GL_DETAIL_ACCOUNT_TABLE}_raw`
export const QBO_FPA_CACHE_META_TABLE = 'fg_qbo_fpa_cache_meta'
export const QBO_GL_DETAIL_SEARCH_COLUMN = '__fg_search_text'

const QBO_GL_DETAIL_CANONICAL_CACHE_VERSION = 'gl-canonical-v6'
const QBO_GL_DETAIL_SEARCH_TEXT_COLUMNS = [
  'transaction_type',
  'num',
  'name',
  'account',
  'account_name',
  'account_id',
  'account_number',
  'customer',
  'customer_id',
  'vendor',
  'vendor_id',
  'employee',
  'employee_id',
  'class',
  'class_id',
  'department',
  'department_id',
  'product_service',
  'item_id',
  'split_account',
  'invoice_id',
  'invoice_num',
  'sales_term_id',
  'sales_term_name',
  'terms_source',
  'ar_paid',
  'memo',
  'tax_code',
] as const
const QBO_GL_DETAIL_SEARCH_TEXT_BATCH_SIZE = 6

export const DEFAULT_GL_DETAIL_COLUMNS = [
  'txn_date',
  'transaction_type',
  'num',
  'name',
  'account_number',
  'account_name',
  'memo',
  'amount',
] as const

export const GL_DETAIL_COLUMN_LABELS: Record<string, string> = {
  txn_date: 'Date',
  period_start: 'Period',
  transaction_type: 'Type',
  type: 'Type',
  num: 'Num',
  journal_entry_id: 'Journal',
  journal_line_id: 'Line',
  name: 'Name',
  customer: 'Customer',
  customer_id: 'Customer ID',
  vendor: 'Vendor',
  vendor_id: 'Vendor ID',
  employee: 'Employee',
  employee_id: 'Employee ID',
  class: 'Class',
  klass: 'Class',
  class_id: 'Class ID',
  department: 'Location',
  location: 'Location',
  department_id: 'Location ID',
  product_service: 'Product',
  item: 'Product',
  item_id: 'Product ID',
  account: 'Account',
  account_name: 'Account name',
  account_id: 'Account ID',
  account_number: 'Account #',
  split_account: 'Split',
  invoice_id: 'Invoice ID',
  invoice_num: 'Invoice #',
  invoice_date: 'Invoice Date',
  invoice_due_date: 'Due Date',
  sales_term_id: 'Term ID',
  sales_term_name: 'Terms',
  term_days: 'Term Days',
  terms_source: 'Terms Source',
  ar_paid: 'A/R Paid',
  open_balance: 'Open Balance',
  memo: 'Memo',
  quantity: 'Qty',
  rate: 'Rate',
  tax_code: 'Tax Code',
  tax_amount: 'Tax',
  net_amount: 'Net',
  debit: 'Debit',
  credit: 'Credit',
  amount: 'Amount',
  source: 'Source',
  is_adjustment: 'Adj',
}

export const GL_DETAIL_NUMERIC_COLUMNS = new Set([
  'debit',
  'credit',
  'amount',
  'quantity',
  'rate',
  'tax_amount',
  'net_amount',
  'term_days',
  'open_balance',
])

export const QBO_FPA_ARTIFACT_NUMERIC_COLUMNS = new Set([
  ...GL_DETAIL_NUMERIC_COLUMNS,
  'ending_balance',
  'monthly_activity',
  'value',
  'sales',
  'revenue',
  'gross_sales',
  'net_sales',
  'invoice_amount',
  'invoice_balance',
  'original_amount',
  'sales_amount',
  'payment_amount',
  'applied_amount',
  'unapplied_amount',
  'remaining_amount',
  'outstanding_amount',
  'current_ar',
  'under90_ar',
  'over60_ar',
  'over90_ar',
  'missing_due_ar',
  'weighted_average_age',
  'source_bucket_total_ar',
  'aging_bucket_total_ar',
  'unclassified_ar',
  'source_excess_ar',
  'bucket_reconciliation_ar',
  'known_reconciling_difference_ar',
  'invoice_count',
  'customer_count',
  'row_count',
])

export const GL_DETAIL_PREFERRED_COLUMNS = [
  'txn_date',
  'transaction_type',
  'num',
  'name',
  'customer',
  'vendor',
  'employee',
  'department',
  'class',
  'product_service',
  'account_number',
  'account_name',
  'account',
  'split_account',
  'invoice_num',
  'invoice_date',
  'invoice_due_date',
  'sales_term_name',
  'term_days',
  'open_balance',
  'memo',
  'quantity',
  'rate',
  'tax_code',
  'tax_amount',
  'net_amount',
  'debit',
  'credit',
  'amount',
  'source',
  'is_adjustment',
] as const

export function trimGlStr(value: unknown): string {
  return String(value ?? '').trim()
}

export function parseNumberish(raw: unknown): number {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  const original = String(raw ?? '').trim()
  const accountingNegative = /^\(.*\)$/.test(original)
  const text = original.replace(/,/g, '').replace(/^\((.*)\)$/, '$1').replace(/[$£€¥]/g, '').trim()
  if (!text) return 0
  const n = Number(text)
  return Number.isFinite(n) ? (accountingNegative ? -n : n) : 0
}

export function glDetailColumnLabel(columnId: string): string {
  return GL_DETAIL_COLUMN_LABELS[columnId] || columnId.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase())
}

export function normalizeGlDetailColumnId(raw: string): string {
  const normalized = String(raw || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toLowerCase()
  const aliases: Record<string, string> = {
    date: 'txn_date',
    transaction_date: 'txn_date',
    transactiontype: 'transaction_type',
    transactiontypeid: 'transaction_type_id',
    doc_number: 'num',
    docnumber: 'num',
    accountid: 'account_id',
    accountname: 'account_name',
    account_: 'account_number',
    account_num: 'account_number',
    accountnum: 'account_number',
    acct_num: 'account_number',
    acctnum: 'account_number',
    customerid: 'customer_id',
    vendorid: 'vendor_id',
    employeeid: 'employee_id',
    classid: 'class_id',
    klass_id: 'class_id',
    departmentid: 'department_id',
    locationid: 'department_id',
    location_id: 'department_id',
    productservice: 'product_service',
    item: 'product_service',
    itemid: 'item_id',
    split: 'split_account',
    invoice_num: 'invoice_num',
    invoicenum: 'invoice_num',
    invoice_due_date: 'invoice_due_date',
    invoiceduedate: 'invoice_due_date',
    due_date: 'invoice_due_date',
    sales_term_name: 'sales_term_name',
    salestermname: 'sales_term_name',
    termdays: 'term_days',
    taxamount: 'tax_amount',
    netamount: 'net_amount',
    openbalance: 'open_balance',
  }
  return aliases[normalized] || normalized
}

export function normalizeGlDetailRow(row: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {}
  for (const [key, value] of Object.entries(row || {})) {
    const normalized = normalizeGlDetailColumnId(key)
    if (normalized) out[normalized] = value
  }
  if (!out.txn_date) out.txn_date = out.date || out.transaction_date || ''
  if (!out.account_name && out.account) {
    const split = splitJournalAccountNumberAndName(String(out.account), String(out.account_number || ''))
    out.account_number ||= split.accountNumber
    out.account_name = split.accountName
  }
  for (const column of GL_DETAIL_NUMERIC_COLUMNS) {
    if (Object.prototype.hasOwnProperty.call(out, column)) out[column] = parseNumberish(out[column])
  }
  return out
}

export function glDetailColumnsFromRows(rows: Record<string, any>[], visibleColumns?: string[]): string[] {
  const seen = new Set<string>()
  for (const row of rows) {
    Object.keys(normalizeGlDetailRow(row)).forEach((column) => {
      if (column && !column.startsWith('_')) seen.add(column)
    })
  }
  for (const column of visibleColumns || []) {
    const normalized = normalizeGlDetailColumnId(column)
    if (normalized) seen.add(normalized)
  }
  const preferred = GL_DETAIL_PREFERRED_COLUMNS.filter((column) => seen.has(column))
  const rest = Array.from(seen)
    .filter((column) => !GL_DETAIL_PREFERRED_COLUMNS.includes(column as any))
    .sort((a, b) => glDetailColumnLabel(a).localeCompare(glDetailColumnLabel(b)))
  return [...preferred, ...rest]
}

export function previewRowsAsObjects(result: any): Record<string, any>[] {
  const headers = Array.isArray(result?.headers) ? result.headers.map((h: any) => String(h || '')) : []
  const rows = Array.isArray(result?.preview_rows) ? result.preview_rows : (Array.isArray(result?.rows) ? result.rows : [])
  if (!headers.length) {
    return rows.map((row: any) => (row && typeof row === 'object' && !Array.isArray(row)) ? row : {})
  }
  return rows.map((row: any) => {
    if (row && typeof row === 'object' && !Array.isArray(row)) return row
    const values = Array.isArray(row) ? row : []
    const out: Record<string, any> = {}
    headers.forEach((header: string, idx: number) => { out[header] = values[idx] })
    return out
  })
}

export function previewRowValue(row: Record<string, any>, ...keys: string[]): any {
  if (!row || typeof row !== 'object') return undefined
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) return row[key]
  }
  const folded = new Map<string, any>()
  for (const [key, value] of Object.entries(row)) {
    folded.set(String(key).replace(/[_\s]/g, '').toLowerCase(), value)
  }
  for (const key of keys) {
    const value = folded.get(String(key).replace(/[_\s]/g, '').toLowerCase())
    if (value !== undefined) return value
  }
  return undefined
}

export function duckDbIdent(name: string): string {
  return `"${String(name || '').replace(/"/g, '""')}"`
}

export function sqlString(raw: string): string {
  return `'${String(raw ?? '').replace(/'/g, "''")}'`
}

export function numericDuckSql(expr: string): string {
  return `COALESCE(TRY_CAST(REPLACE(REPLACE(REPLACE(CAST(${expr} AS VARCHAR), ',', ''), '$', ''), '£', '') AS DOUBLE), 0)`
}

export function textDuckSql(expr: string): string {
  return `COALESCE(CAST(${expr} AS VARCHAR), '')`
}

export function columnSql(columns: Set<string>, candidates: string[], fallback = 'NULL'): string {
  for (const candidate of candidates) {
    if (columns.has(candidate)) return duckDbIdent(candidate)
  }
  const normalizedByColumn = new Map(Array.from(columns).map((column) => [normalizeGlDetailColumnId(column), column]))
  for (const candidate of candidates) {
    const column = normalizedByColumn.get(normalizeGlDetailColumnId(candidate))
    if (column) return duckDbIdent(column)
  }
  return fallback
}

function summarizeGlColumns(columns: Set<string> | null | undefined): string {
  return Array.from(columns || []).slice(0, 30).join(', ')
}

async function assertGlDetailRowCount(args: {
  workerClient: any
  tableName: string
  expectedRows: number
  columns?: Set<string> | null
  caller: string
  label: string
  opts?: Record<string, any>
}): Promise<number> {
  const rowCount = await tryDuckDbTableRowCountStrict(args.workerClient, args.tableName, args.caller, args.opts)
  if (rowCount === null) return -1
  if (args.expectedRows > 0 && rowCount === 0) {
    throw new Error(`QuickBooks GL detail ${args.label} materialized 0 rows; expected ${args.expectedRows.toLocaleString()}. Columns: ${summarizeGlColumns(args.columns) || '(none)'}`)
  }
  return rowCount
}

export function cacheManifestToPrepareResponse(manifest: QuickBooksFpaCacheManifest): QuickBooksFpaCachePrepareResponse {
  return {
    manifest,
    tables: (manifest.tables || []).map((table): QuickBooksFpaCacheTablePayload => ({ ...table, rows: [] })),
  }
}

export function cacheTableIdMatches(table: Pick<QuickBooksFpaCacheTablePayload, 'id' | 'name'> | null | undefined, id: string): boolean {
  if (!table) return false
  const aliases = new Set([
    id,
    'gl_detail',
    'qbo_fpa_gl_detail',
    'fg_qbo_fpa_gl_detail',
  ])
  return [table.id, table.name].some((value) => aliases.has(trimGlStr(value)))
}

function cacheTableMatchesAliases(table: Pick<QuickBooksFpaCacheTablePayload, 'id' | 'name'> | null | undefined, aliases: string[]): boolean {
  if (!table) return false
  const values = [table.id, table.name].map((value) => trimGlStr(value).toLowerCase()).filter(Boolean)
  const expected = new Set(aliases.map((value) => trimGlStr(value).toLowerCase()).filter(Boolean))
  return values.some((value) => expected.has(value))
}

export function findGlDetailCacheTable(cache: QuickBooksFpaCachePrepareResponse | null | undefined): QuickBooksFpaCacheTablePayload | null {
  return cache?.tables?.find((table) => cacheTableIdMatches(table, 'gl_detail')) || null
}

export function findAccountCacheTable(cache: QuickBooksFpaCachePrepareResponse | null | undefined): QuickBooksFpaCacheTablePayload | null {
  return cache?.tables?.find((table) => cacheTableMatchesAliases(table, [
    'account',
    'accounts',
    'qbo_fpa_account',
    'fg_qbo_fpa_account',
  ])) || null
}

function cacheTableHasLoadableData(table: QuickBooksFpaCacheTablePayload | null | undefined): boolean {
  if (!table) return false
  return Boolean(
    (Array.isArray(table.rows) && table.rows.length)
      || table.parquet_url
      || (Array.isArray(table.parquet_urls) && table.parquet_urls.some(Boolean))
      || table.artifact_uri
      || (Array.isArray(table.artifact_uris) && table.artifact_uris.some(Boolean)),
  )
}

export async function hydrateGlDetailCacheTable(
  table: QuickBooksFpaCacheTablePayload,
  appApi: ServerApiClient | undefined,
  connectionId: string,
): Promise<QuickBooksFpaCacheTablePayload> {
  if (cacheTableHasLoadableData(table)) return table
  if (!appApi || typeof appApi.getQuickBooksFpaCacheFile !== 'function' || !connectionId) return table
  const rawTableId = trimGlStr(table.id || table.name || 'gl_detail')
  const fileId = cacheTableIdMatches(table, 'gl_detail') ? 'gl_detail' : rawTableId
  if (!fileId) return table
  const hydrated = await appApi.getQuickBooksFpaCacheFile(connectionId, fileId)
  return {
    ...table,
    ...hydrated,
    id: hydrated.id || table.id,
    name: hydrated.name || table.name,
    row_count: hydrated.row_count ?? table.row_count,
    row_count_hint: hydrated.row_count_hint ?? table.row_count_hint,
  }
}

export function cacheTablePartBytes(table: QuickBooksFpaCacheTablePayload | null | undefined, partCount = 0): number[] {
  if (!table) return []
  const raw = Array.isArray(table.parquet_part_bytes)
    ? table.parquet_part_bytes
    : Array.isArray(table.artifact_part_bytes)
      ? table.artifact_part_bytes
      : []
  const values = raw.map((value) => Math.max(0, Math.floor(Number(value) || 0))).filter((value) => value > 0)
  if (partCount <= 0) return values
  if (values.length > 1 && partCount === 1) return []
  return values.slice(0, partCount)
}

export function quickBooksGlDetailParquetUrls(
  api: ServerApiClient | undefined,
  connectionId: string | undefined,
  table: QuickBooksFpaCacheTablePayload | null,
): string[] {
  if (!table) return []
  const declaredParts = Array.isArray(table.parquet_urls) ? table.parquet_urls.filter(Boolean) : []
  const artifactParts = Array.isArray(table.artifact_uris) ? table.artifact_uris.filter(Boolean) : []
  const hasMergedParquetUrl = Boolean(trimGlStr(table.parquet_url))
  const partCount = hasMergedParquetUrl && !declaredParts.length ? 1 : declaredParts.length || artifactParts.length
  if (api && connectionId && table.id) {
    if (partCount > 1) {
      return Array.from({ length: partCount }, (_value, index) => api.getQuickBooksFpaCacheParquetUrl(connectionId, table.id, index))
    }
    if (partCount === 1 || table.parquet_url || table.artifact_uri) {
      return [api.getQuickBooksFpaCacheParquetUrl(connectionId, table.id)]
    }
  }
  if (declaredParts.length) return declaredParts
  return table.parquet_url ? [table.parquet_url] : []
}

export function cacheTableSignature(table: QuickBooksFpaCacheTablePayload | null | undefined, fallbackId = 'gl_detail'): string {
  if (!table) return ''
  const urls = quickBooksGlDetailParquetUrls(undefined, undefined, table)
  const rowCount = table.row_count_hint ?? table.row_count ?? table.rows?.length ?? 0
  return [
    table.id || table.name || fallbackId,
    table.extract_version_id || '',
    rowCount,
    urls.length,
    ...cacheTablePartBytes(table, urls.length),
  ].join('~')
}

export function splitJournalAccountNumberAndName(rawAccount: string | null | undefined, fallbackNumber = ''): { accountNumber: string; accountName: string } {
  const text = String(rawAccount || '').trim()
  const fallback = String(fallbackNumber || '').trim()
  if (!text) return { accountNumber: fallback, accountName: '' }
  if (fallback) {
    const escapedFallback = fallback.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const stripped = text.replace(new RegExp(`^${escapedFallback}\\s*(?:[-:|]\\s*)?`, 'i'), '').trim()
    return { accountNumber: fallback, accountName: stripped || text }
  }
  const match = text.match(/^([A-Za-z]*\d[A-Za-z0-9]*(?:[-.][A-Za-z0-9]+)*)(?:\s+|\s*[-:|]\s+)(.+)$/)
  if (!match) return { accountNumber: '', accountName: text }
  return { accountNumber: match[1].trim(), accountName: match[2].trim() || text }
}

export async function duckDbTableColumns(workerClient: any, tableName: string, opts?: Record<string, any>): Promise<Set<string>> {
  const result = await workerClient.duckdb_run_sql(`SELECT * FROM ${duckDbIdent(tableName)} LIMIT 0`, {
    ...(opts || {}),
    previewRowLimit: 0,
    timeoutMs: 60000,
    caller: 'QuickBooksCfoCore:gl_detail_columns',
  })
  return new Set((Array.isArray(result?.headers) ? result.headers : []).map((header: any) => String(header || '')).filter(Boolean))
}

async function tryDuckDbTableColumns(workerClient: any, tableName: string, opts?: Record<string, any>): Promise<Set<string> | null> {
  try {
    const result = await workerClient.duckdb_run_sql(
      `SELECT column_name
FROM information_schema.columns
WHERE table_name = ${sqlString(tableName)}
ORDER BY ordinal_position`,
      {
        ...(opts || {}),
        previewRowLimit: 500,
        timeoutMs: 60000,
        caller: 'QuickBooksCfoCore:gl_detail_columns_probe',
      },
    )
    const columns = new Set(
      previewRowsAsObjects(result)
        .map((row) => String(previewRowValue(row, 'column_name', 'COLUMN_NAME') || '').trim())
        .filter(Boolean),
    )
    return columns.size ? columns : null
  } catch {
    return null
  }
}

function glDetailColumnsAreCanonical(columns: Set<string> | null): boolean {
  if (!columns?.size) return false
  return ['txn_date', 'transaction_type', 'account_name', 'amount'].every((column) => columns.has(column))
}

function glDetailColumnsAreQueryReady(columns: Set<string> | null): boolean {
  return glDetailColumnsAreCanonical(columns) && Boolean(columns?.has(QBO_GL_DETAIL_SEARCH_COLUMN))
}

async function runMutating(workerClient: any, sql: string, caller: string, opts?: Record<string, any>): Promise<any> {
  return workerClient.duckdb_run_sql(sql, {
    ...(opts || {}),
    allowMutating: true,
    cacheExtracts: true,
    interruptPrevious: false,
    previewRowLimit: 100,
    timeoutMs: 180000,
    caller,
  })
}

async function dropDuckDbRelation(workerClient: any, tableName: string, caller: string, opts?: Record<string, any>): Promise<void> {
  const relationName = sqlString(tableName)
  const relationRows = await workerClient.duckdb_run_sql(
    `SELECT table_type
FROM information_schema.tables
WHERE table_name = ${relationName}
LIMIT 1`,
    {
      ...(opts || {}),
      previewRowLimit: 1,
      timeoutMs: 60000,
      caller: `${caller}_probe`,
    },
  )
  const relationType = String(previewRowsAsObjects(relationRows)[0]?.table_type || '').toUpperCase()
  if (relationType.includes('VIEW')) {
    await runMutating(workerClient, `DROP VIEW IF EXISTS ${duckDbIdent(tableName)};`, caller, opts)
    return
  }
  if (relationType) {
    await runMutating(workerClient, `DROP TABLE IF EXISTS ${duckDbIdent(tableName)};`, caller, opts)
    return
  }
  await runMutating(
    workerClient,
    `DROP TABLE IF EXISTS ${duckDbIdent(tableName)};`,
    caller,
    opts,
  )
}

async function duckDbTableRowCount(workerClient: any, tableName: string, caller: string, opts?: Record<string, any>): Promise<number> {
  try {
    const result = await workerClient.duckdb_run_sql(
      `SELECT COUNT(*) AS row_count FROM ${duckDbIdent(tableName)}`,
      {
        ...(opts || {}),
        previewRowLimit: 1,
        timeoutMs: 60000,
        caller,
      },
    )
    const row = previewRowsAsObjects(result)[0] || {}
    const count = Number(previewRowValue(row, 'row_count', 'rowCount', 'count', 'COUNT', 'ROW_COUNT') ?? 0)
    return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0
  } catch {
    return 0
  }
}

async function tryDuckDbTableRowCountStrict(workerClient: any, tableName: string, caller: string, opts?: Record<string, any>): Promise<number | null> {
  try {
    const result = await workerClient.duckdb_run_sql(
      `SELECT COUNT(*) AS row_count FROM ${duckDbIdent(tableName)}`,
      {
        ...(opts || {}),
        previewRowLimit: 1,
        timeoutMs: 60000,
        caller,
      },
    )
    const row = previewRowsAsObjects(result)[0] || {}
    const hasCountHeader = Array.isArray(result?.headers)
      ? result.headers.some((header: any) => ['row_count', 'rowCount', 'count', 'COUNT', 'ROW_COUNT'].includes(String(header || '')))
      : Object.keys(row).some((key) => ['row_count', 'rowCount', 'count', 'COUNT', 'ROW_COUNT'].includes(key))
    if (!hasCountHeader) return null
    const count = Number(previewRowValue(row, 'row_count', 'rowCount', 'count', 'COUNT', 'ROW_COUNT') ?? 0)
    return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null
  } catch {
    return null
  }
}

function glDetailCanonicalCacheKey(args: {
  connectionId: string
  cache: QuickBooksFpaCachePrepareResponse
  table: QuickBooksFpaCacheTablePayload
  accountTable?: QuickBooksFpaCacheTablePayload | null
}): string {
  return [
    args.connectionId,
    args.cache.manifest.cache_dataset_id || '',
    cacheTableSignature(args.table),
    cacheTableSignature(args.accountTable, 'account'),
    QBO_GL_DETAIL_CANONICAL_CACHE_VERSION,
  ].join('|')
}

async function readQuickBooksFpaDuckDbMaterializedKey(workerClient: any, tableName: string, opts?: Record<string, any>): Promise<string> {
  try {
    const result = await workerClient.duckdb_run_sql(
      `SELECT cache_key FROM ${duckDbIdent(QBO_FPA_CACHE_META_TABLE)} WHERE table_name = ${sqlString(tableName)} LIMIT 1`,
      {
        ...(opts || {}),
        previewRowLimit: 1,
        timeoutMs: 30000,
        caller: 'QuickBooksCfoCore:gl_detail_cache_meta_read',
      },
    )
    const row = previewRowsAsObjects(result)[0] || {}
    return String(previewRowValue(row, 'cache_key', 'cacheKey', 'CACHE_KEY') || '').trim()
  } catch {
    return ''
  }
}

async function markQuickBooksFpaDuckDbTableMaterialized(
  workerClient: any,
  tableName: string,
  cacheKey: string,
  opts?: Record<string, any>,
): Promise<void> {
  await runMutating(
    workerClient,
    `CREATE TABLE IF NOT EXISTS ${duckDbIdent(QBO_FPA_CACHE_META_TABLE)} (table_name VARCHAR, cache_key VARCHAR, recorded_at VARCHAR)`,
    'QuickBooksCfoCore:gl_detail_cache_meta_create',
    opts,
  )
  await runMutating(
    workerClient,
    `DELETE FROM ${duckDbIdent(QBO_FPA_CACHE_META_TABLE)} WHERE table_name = ${sqlString(tableName)}`,
    'QuickBooksCfoCore:gl_detail_cache_meta_delete',
    opts,
  )
  await runMutating(
    workerClient,
    `INSERT INTO ${duckDbIdent(QBO_FPA_CACHE_META_TABLE)} VALUES (${sqlString(tableName)}, ${sqlString(cacheKey)}, ${sqlString(new Date().toISOString())})`,
    'QuickBooksCfoCore:gl_detail_cache_meta_insert',
    opts,
  )
}

async function reusableQuickBooksGlDetailTable(args: {
  workerClient: any
  columns: Set<string> | null
  cacheKey: string
  expectedRows: number
  opts?: Record<string, any>
}): Promise<boolean> {
  if (!glDetailColumnsAreQueryReady(args.columns)) return false
  const recordedKey = await readQuickBooksFpaDuckDbMaterializedKey(args.workerClient, QBO_GL_DETAIL_TABLE, args.opts)
  const expectedRows = Math.max(0, Math.floor(Number(args.expectedRows || 0)))
  if (recordedKey === args.cacheKey) {
    if (expectedRows <= 0) return true
    const rowCount = await duckDbTableRowCount(
      args.workerClient,
      QBO_GL_DETAIL_TABLE,
      'QuickBooksCfoCore:gl_detail_reuse_row_count',
      args.opts,
    )
    return rowCount === expectedRows
  }
  if (recordedKey || expectedRows <= 0) return false
  const rowCount = await duckDbTableRowCount(
    args.workerClient,
    QBO_GL_DETAIL_TABLE,
    'QuickBooksCfoCore:gl_detail_reuse_row_count',
    args.opts,
  )
  if (rowCount !== expectedRows) return false
  await markQuickBooksFpaDuckDbTableMaterialized(args.workerClient, QBO_GL_DETAIL_TABLE, args.cacheKey, args.opts)
  return true
}

async function createRawGlDetailCopy(workerClient: any, sourceTableName: string, opts?: Record<string, any>): Promise<Set<string>> {
  await dropDuckDbRelation(workerClient, QBO_GL_DETAIL_RAW_TABLE, 'QuickBooksCfoCore:gl_detail_explore_raw_drop', opts)
  await runMutating(
    workerClient,
    `CREATE TABLE ${duckDbIdent(QBO_GL_DETAIL_RAW_TABLE)} AS SELECT * FROM ${duckDbIdent(sourceTableName)}`,
    'QuickBooksCfoCore:gl_detail_explore_raw_copy',
    opts,
  )
  return duckDbTableColumns(workerClient, QBO_GL_DETAIL_RAW_TABLE, opts)
}

export function normalizeAccountLookupRow(row: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {}
  for (const [key, value] of Object.entries(row || {})) {
    const normalized = normalizeGlDetailColumnId(key)
    if (normalized) out[normalized] = value
  }
  out.account_id ||= previewRowValue(row, 'account_id', 'AccountId', 'AccountID', 'Id', 'id')
  out.account_number ||= previewRowValue(row, 'account_number', 'AccountNumber', 'Account Number', 'AccountNum', 'Account Num', 'AcctNum', 'Account_')
  out.account_name ||= previewRowValue(row, 'account_name', 'AccountName', 'Name', 'name')
  out.fully_qualified_name ||= previewRowValue(row, 'fully_qualified_name', 'FullyQualifiedName', 'Fully Qualified Name')
  return out
}

function buildCanonicalAccountLookupSql(columns: Set<string>, rawTableName = QBO_GL_DETAIL_ACCOUNT_RAW_TABLE): string {
  const accountId = columnSql(columns, ['account_id', 'AccountId', 'AccountID', 'Id', 'id'])
  const accountNumber = columnSql(columns, ['account_number', 'AccountNumber', 'Account Number', 'AccountNum', 'Account Num', 'AcctNum', 'Account_'], "''")
  const accountName = columnSql(columns, ['account_name', 'AccountName', 'Name', 'name'], "''")
  const fullyQualifiedName = columnSql(columns, ['fully_qualified_name', 'FullyQualifiedName', 'Fully Qualified Name'], accountName)
  return `
CREATE OR REPLACE TABLE ${duckDbIdent(QBO_GL_DETAIL_ACCOUNT_TABLE)} AS
SELECT
  ${textDuckSql(accountId)} AS account_id,
  ${textDuckSql(accountNumber)} AS account_number,
  ${textDuckSql(accountName)} AS account_name,
  ${textDuckSql(fullyQualifiedName)} AS fully_qualified_name
FROM ${duckDbIdent(rawTableName)}
WHERE NULLIF(${textDuckSql(accountId)}, '') IS NOT NULL`
}

async function canonicalizeRawAccountLookup(workerClient: any, columns: Set<string>, opts?: Record<string, any>): Promise<string> {
  await dropDuckDbRelation(workerClient, QBO_GL_DETAIL_ACCOUNT_TABLE, 'QuickBooksCfoCore:gl_detail_account_target_drop', opts)
  await runMutating(
    workerClient,
    buildCanonicalAccountLookupSql(columns, QBO_GL_DETAIL_ACCOUNT_RAW_TABLE),
    'QuickBooksCfoCore:gl_detail_account_canonical',
    opts,
  )
  return QBO_GL_DETAIL_ACCOUNT_TABLE
}

async function materializeQuickBooksAccountLookupTable(args: {
  workerClient: any
  appApi?: ServerApiClient
  connectionId: string
  cache: QuickBooksFpaCachePrepareResponse
  duckDbOpts?: Record<string, any> | null
}): Promise<string | null> {
  const { workerClient, appApi, connectionId, cache, duckDbOpts } = args
  const manifestTable = findAccountCacheTable(cache)
  if (!manifestTable) return null
  const table = await hydrateGlDetailCacheTable(manifestTable, appApi, connectionId)
  const rows = Array.isArray(table.rows) ? table.rows.map((row) => normalizeAccountLookupRow(row)) : []
  const urls = quickBooksGlDetailParquetUrls(appApi, connectionId, table)
  if (urls.length) {
    if (typeof workerClient.duckdb_create_table_from_parquet_urls !== 'function') return null
    await dropDuckDbRelation(workerClient, QBO_GL_DETAIL_ACCOUNT_RAW_TABLE, 'QuickBooksCfoCore:gl_detail_account_raw_drop', duckDbOpts || undefined)
    await workerClient.duckdb_create_table_from_parquet_urls(QBO_GL_DETAIL_ACCOUNT_RAW_TABLE, urls, {
      ...(duckDbOpts || {}),
      persistent: true,
      caller: 'QuickBooksCfoCore:gl_detail_account_parquet',
      sharedPartCacheKey: [connectionId, cache.manifest.cache_dataset_id || '', cacheTableSignature(table, 'account')].join('|'),
      partBytes: cacheTablePartBytes(table, urls.length),
      timeoutMs: 180000,
    })
    const columns = await duckDbTableColumns(workerClient, QBO_GL_DETAIL_ACCOUNT_RAW_TABLE, duckDbOpts || undefined)
    return canonicalizeRawAccountLookup(workerClient, columns, duckDbOpts || undefined)
  }
  if (!rows.length || typeof workerClient.duckdb_create_temp_table_from_rows !== 'function') return null
  await dropDuckDbRelation(workerClient, QBO_GL_DETAIL_ACCOUNT_RAW_TABLE, 'QuickBooksCfoCore:gl_detail_account_raw_drop', duckDbOpts || undefined)
  const columns = glDetailColumnsFromRows(rows, ['account_id', 'account_number', 'account_name', 'fully_qualified_name'])
  await workerClient.duckdb_create_temp_table_from_rows(QBO_GL_DETAIL_ACCOUNT_RAW_TABLE, columns, rows, {
    ...(duckDbOpts || {}),
    persistent: true,
    caller: 'QuickBooksCfoCore:gl_detail_account_rows',
    dropIfExists: true,
    maxRows: Math.max(1000, rows.length),
    chunkSize: 1000,
    timeoutMs: 60000,
  })
  const rawColumns = await duckDbTableColumns(workerClient, QBO_GL_DETAIL_ACCOUNT_RAW_TABLE, duckDbOpts || undefined)
  return canonicalizeRawAccountLookup(workerClient, rawColumns, duckDbOpts || undefined)
}

async function canonicalizeRawGlDetail(
  workerClient: any,
  columns: Set<string>,
  opts?: Record<string, any>,
  accountLookupTableName?: string | null,
): Promise<void> {
  await dropDuckDbRelation(workerClient, QBO_GL_DETAIL_TABLE, 'QuickBooksCfoCore:gl_detail_explore_target_drop', opts)
  await runMutating(
    workerClient,
    buildCanonicalGlDetailSql(columns, QBO_GL_DETAIL_RAW_TABLE, accountLookupTableName || null),
    'QuickBooksCfoCore:gl_detail_explore_canonical',
    opts,
  )
  for (const sql of buildCanonicalGlDetailSearchTextSql()) {
    await runMutating(
      workerClient,
      sql,
      'QuickBooksCfoCore:gl_detail_explore_search_text',
      opts,
    )
  }
}

export async function materializeQuickBooksGlDetailTable(args: {
  workerClient: any
  appApi?: ServerApiClient
  connectionId: string
  cache: QuickBooksFpaCachePrepareResponse
  duckDbOpts?: Record<string, any> | null
}): Promise<{ tableName: string; rowCountHint: number; columns: string[] }> {
  const { workerClient, appApi, connectionId, cache, duckDbOpts } = args
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') {
    throw new Error('Local DuckDB SQL is not available for GL detail.')
  }
  const manifestTable = findGlDetailCacheTable(cache)
  if (!manifestTable) throw new Error('QuickBooks GL detail cache table is not available yet.')
  const table = await hydrateGlDetailCacheTable(manifestTable, appApi, connectionId)
  const rows = Array.isArray(table.rows) ? table.rows.map((row) => normalizeGlDetailRow(row)) : []
  const urls = quickBooksGlDetailParquetUrls(appApi, connectionId, table)
  const rowCountHint = Math.max(0, Number(table.row_count_hint ?? table.row_count ?? rows.length ?? 0) || 0)
  const accountManifestTable = findAccountCacheTable(cache)
  const canonicalCacheKey = glDetailCanonicalCacheKey({
    connectionId,
    cache,
    table,
    accountTable: accountManifestTable,
  })
  const accountLookupTableName = await materializeQuickBooksAccountLookupTable({
    workerClient,
    appApi,
    connectionId,
    cache,
    duckDbOpts,
  })
  const existingColumns = await tryDuckDbTableColumns(workerClient, QBO_GL_DETAIL_TABLE, duckDbOpts || undefined)
  const existingRawColumns = await tryDuckDbTableColumns(workerClient, QBO_GL_DETAIL_RAW_TABLE, duckDbOpts || undefined)
  const existingRawRowCount = existingRawColumns?.size
    ? await duckDbTableRowCount(workerClient, QBO_GL_DETAIL_RAW_TABLE, 'QuickBooksCfoCore:gl_detail_raw_reuse_row_count', duckDbOpts || undefined)
    : 0
  if (await reusableQuickBooksGlDetailTable({
    workerClient,
    columns: existingColumns,
    cacheKey: canonicalCacheKey,
    expectedRows: rowCountHint,
    opts: duckDbOpts || undefined,
  })) {
    return { tableName: QBO_GL_DETAIL_TABLE, rowCountHint, columns: glDetailColumnsFromRows([], Array.from(existingColumns!)) }
  }
  const existingCanonicalRowCount = existingColumns?.size && rowCountHint > 0
    ? await duckDbTableRowCount(workerClient, QBO_GL_DETAIL_TABLE, 'QuickBooksCfoCore:gl_detail_existing_row_count', duckDbOpts || undefined)
    : 0
  const existingCanonicalRowsMatch = rowCountHint <= 0 || existingCanonicalRowCount === rowCountHint
  if (existingRawColumns?.size && (rowCountHint <= 0 || existingRawRowCount === rowCountHint)) {
    await canonicalizeRawGlDetail(workerClient, existingRawColumns, duckDbOpts || undefined, accountLookupTableName)
    const finalColumns = await duckDbTableColumns(workerClient, QBO_GL_DETAIL_TABLE, duckDbOpts || undefined)
    await assertGlDetailRowCount({
      workerClient,
      tableName: QBO_GL_DETAIL_TABLE,
      expectedRows: rowCountHint,
      columns: finalColumns,
      caller: 'QuickBooksCfoCore:gl_detail_canonical_reuse_row_count',
      label: 'canonical table',
      opts: duckDbOpts || undefined,
    })
    await markQuickBooksFpaDuckDbTableMaterialized(workerClient, QBO_GL_DETAIL_TABLE, canonicalCacheKey, duckDbOpts || undefined)
    return { tableName: QBO_GL_DETAIL_TABLE, rowCountHint, columns: glDetailColumnsFromRows([], Array.from(finalColumns)) }
  }
  if (existingCanonicalRowsMatch && glDetailColumnsAreQueryReady(existingColumns) && accountLookupTableName && existingColumns?.has('account_id')) {
    const copiedColumns = await createRawGlDetailCopy(workerClient, QBO_GL_DETAIL_TABLE, duckDbOpts || undefined)
    await canonicalizeRawGlDetail(workerClient, copiedColumns, duckDbOpts || undefined, accountLookupTableName)
    const finalColumns = await duckDbTableColumns(workerClient, QBO_GL_DETAIL_TABLE, duckDbOpts || undefined)
    await assertGlDetailRowCount({
      workerClient,
      tableName: QBO_GL_DETAIL_TABLE,
      expectedRows: rowCountHint,
      columns: finalColumns,
      caller: 'QuickBooksCfoCore:gl_detail_canonical_account_refresh_row_count',
      label: 'canonical table',
      opts: duckDbOpts || undefined,
    })
    await markQuickBooksFpaDuckDbTableMaterialized(workerClient, QBO_GL_DETAIL_TABLE, canonicalCacheKey, duckDbOpts || undefined)
    return { tableName: QBO_GL_DETAIL_TABLE, rowCountHint, columns: glDetailColumnsFromRows([], Array.from(finalColumns)) }
  }
  if (existingCanonicalRowsMatch && glDetailColumnsAreQueryReady(existingColumns)) {
    await markQuickBooksFpaDuckDbTableMaterialized(workerClient, QBO_GL_DETAIL_TABLE, canonicalCacheKey, duckDbOpts || undefined)
    return { tableName: QBO_GL_DETAIL_TABLE, rowCountHint, columns: glDetailColumnsFromRows([], Array.from(existingColumns!)) }
  }
  if (existingCanonicalRowsMatch && existingColumns?.size) {
    const copiedColumns = await createRawGlDetailCopy(workerClient, QBO_GL_DETAIL_TABLE, duckDbOpts || undefined)
    await canonicalizeRawGlDetail(workerClient, copiedColumns, duckDbOpts || undefined, accountLookupTableName)
    const finalColumns = await duckDbTableColumns(workerClient, QBO_GL_DETAIL_TABLE, duckDbOpts || undefined)
    await assertGlDetailRowCount({
      workerClient,
      tableName: QBO_GL_DETAIL_TABLE,
      expectedRows: rowCountHint,
      columns: finalColumns,
      caller: 'QuickBooksCfoCore:gl_detail_canonical_existing_refresh_row_count',
      label: 'canonical table',
      opts: duckDbOpts || undefined,
    })
    await markQuickBooksFpaDuckDbTableMaterialized(workerClient, QBO_GL_DETAIL_TABLE, canonicalCacheKey, duckDbOpts || undefined)
    return { tableName: QBO_GL_DETAIL_TABLE, rowCountHint, columns: glDetailColumnsFromRows([], Array.from(finalColumns)) }
  }
  if (urls.length) {
    if (typeof workerClient.duckdb_create_table_from_parquet_urls !== 'function') {
      throw new Error('Local DuckDB parquet materialization is not available for GL detail.')
    }
    await dropDuckDbRelation(workerClient, QBO_GL_DETAIL_RAW_TABLE, 'QuickBooksCfoCore:gl_detail_explore_raw_drop', duckDbOpts || undefined)
    await workerClient.duckdb_create_table_from_parquet_urls(QBO_GL_DETAIL_RAW_TABLE, urls, {
      ...(duckDbOpts || {}),
      persistent: true,
      caller: 'QuickBooksCfoCore:gl_detail_explore_parquet',
      sharedPartCacheKey: [connectionId, cache.manifest.cache_dataset_id || '', cacheTableSignature(table)].join('|'),
      partBytes: cacheTablePartBytes(table, urls.length),
      timeoutMs: 900000,
    })
    const columns = await duckDbTableColumns(workerClient, QBO_GL_DETAIL_RAW_TABLE, duckDbOpts || undefined)
    await assertGlDetailRowCount({
      workerClient,
      tableName: QBO_GL_DETAIL_RAW_TABLE,
      expectedRows: rowCountHint,
      columns,
      caller: 'QuickBooksCfoCore:gl_detail_raw_materialized_row_count',
      label: 'raw Parquet table',
      opts: duckDbOpts || undefined,
    })
    await canonicalizeRawGlDetail(workerClient, columns, duckDbOpts || undefined, accountLookupTableName)
  } else {
    if (!rows.length) throw new Error('QuickBooks GL detail has no rows or parquet artifact to load.')
    if (typeof workerClient.duckdb_create_temp_table_from_rows !== 'function') {
      throw new Error('Local DuckDB row materialization is not available for GL detail.')
    }
    await dropDuckDbRelation(workerClient, QBO_GL_DETAIL_TABLE, 'QuickBooksCfoCore:gl_detail_explore_target_drop', duckDbOpts || undefined)
    const columns = glDetailColumnsFromRows(rows)
    await workerClient.duckdb_create_temp_table_from_rows(QBO_GL_DETAIL_TABLE, columns, rows, {
      ...(duckDbOpts || {}),
      persistent: true,
      caller: 'QuickBooksCfoCore:gl_detail_explore_rows',
      dropIfExists: true,
      maxRows: Math.max(250000, rows.length),
      chunkSize: 1000,
      timeoutMs: 180000,
    })
  }
  const finalColumns = await duckDbTableColumns(workerClient, QBO_GL_DETAIL_TABLE, duckDbOpts || undefined)
  await assertGlDetailRowCount({
    workerClient,
    tableName: QBO_GL_DETAIL_TABLE,
    expectedRows: rowCountHint,
    columns: finalColumns,
    caller: 'QuickBooksCfoCore:gl_detail_canonical_final_row_count',
    label: 'canonical table',
    opts: duckDbOpts || undefined,
  })
  await markQuickBooksFpaDuckDbTableMaterialized(workerClient, QBO_GL_DETAIL_TABLE, canonicalCacheKey, duckDbOpts || undefined)
  return { tableName: QBO_GL_DETAIL_TABLE, rowCountHint, columns: glDetailColumnsFromRows([], Array.from(finalColumns)) }
}

export function buildCanonicalGlDetailSql(columns: Set<string>, rawTableName = QBO_GL_DETAIL_RAW_TABLE, accountLookupTableName?: string | null): string {
  const txnDate = columnSql(columns, ['txn_date', 'date', 'Date', 'TxnDate', 'TransactionDate'])
  const transactionType = columnSql(columns, ['transaction_type', 'transactiontype', 'type', 'TransactionType', 'Type'])
  const num = columnSql(columns, ['num', 'doc_number', 'docnumber', 'Num', 'DocNumber'])
  const name = columnSql(columns, ['name', 'Name'])
  const account = columnSql(columns, ['account', 'Account'])
  const accountName = columnSql(columns, ['account_name', 'AccountName', 'Account_Name'], account)
  const accountId = columnSql(columns, ['account_id', 'AccountId', 'AccountID'])
  const accountNumber = columnSql(columns, ['account_number', 'AccountNumber', 'Account Number', 'AccountNum', 'Account Num', 'AcctNum', 'Account_'], "''")
  const customer = columnSql(columns, ['customer', 'Customer'], "''")
  const customerId = columnSql(columns, ['customer_id', 'CustomerId', 'CustomerID'], "''")
  const vendor = columnSql(columns, ['vendor', 'Vendor'], "''")
  const vendorId = columnSql(columns, ['vendor_id', 'VendorId', 'VendorID'], "''")
  const employee = columnSql(columns, ['employee', 'Employee'], "''")
  const employeeId = columnSql(columns, ['employee_id', 'EmployeeId', 'EmployeeID'], "''")
  const klass = columnSql(columns, ['class', 'Class', 'klass', 'Klass'], "''")
  const classId = columnSql(columns, ['class_id', 'ClassId', 'klass_id', 'KlassId'], "''")
  const department = columnSql(columns, ['department', 'Department', 'location', 'Location'], "''")
  const departmentId = columnSql(columns, ['department_id', 'DepartmentId', 'location_id', 'LocationId'], "''")
  const productService = columnSql(columns, ['product_service', 'Product_Service', 'ProductService', 'item', 'Item'], "''")
  const itemId = columnSql(columns, ['item_id', 'ItemId', 'Product_ServiceId', 'ProductServiceId'], "''")
  const splitAccount = columnSql(columns, ['split_account', 'SplitAccount', 'Split'], "''")
  const invoiceId = columnSql(columns, ['invoice_id', 'InvoiceId'], "''")
  const invoiceNum = columnSql(columns, ['invoice_num', 'InvoiceNum'], "''")
  const invoiceDate = columnSql(columns, ['invoice_date', 'InvoiceDate', 'InvoiceTxnDate'], "''")
  const invoiceDueDate = columnSql(columns, ['invoice_due_date', 'InvoiceDueDate', 'DueDate'], "''")
  const salesTermId = columnSql(columns, ['sales_term_id', 'SalesTermId'], "''")
  const salesTermName = columnSql(columns, ['sales_term_name', 'SalesTermName'], "''")
  const termDays = columnSql(columns, ['term_days', 'TermDays'], '0')
  const termsSource = columnSql(columns, ['terms_source', 'TermsSource'], "''")
  const arPaid = columnSql(columns, ['ar_paid', 'A_RPaid', 'ARPaid'], "''")
  const openBalance = columnSql(columns, ['open_balance', 'OpenBalance'], '0')
  const memo = columnSql(columns, ['memo', 'Memo', 'Description'], "''")
  const quantity = columnSql(columns, ['quantity', 'Quantity', 'Qty'], '0')
  const rate = columnSql(columns, ['rate', 'Rate'], '0')
  const taxCode = columnSql(columns, ['tax_code', 'TaxCode'], "''")
  const taxAmount = columnSql(columns, ['tax_amount', 'TaxAmount'], '0')
  const netAmount = columnSql(columns, ['net_amount', 'NetAmount'], '0')
  const debit = columnSql(columns, ['debit', 'Debit'], '0')
  const credit = columnSql(columns, ['credit', 'Credit'], '0')
  const amount = columnSql(columns, ['amount', 'Amount'], `(${numericDuckSql(debit)} - ${numericDuckSql(credit)})`)
  const accountNumberExpr = accountLookupTableName
    ? `COALESCE(NULLIF(${textDuckSql(accountNumber)}, ''), NULLIF(ac.account_number, ''))`
    : textDuckSql(accountNumber)
  const accountJoin = accountLookupTableName
    ? `
LEFT JOIN ${duckDbIdent(accountLookupTableName)} ac
  ON NULLIF(${textDuckSql(accountId)}, '') = ac.account_id`
    : ''
  return `
CREATE OR REPLACE TABLE ${duckDbIdent(QBO_GL_DETAIL_TABLE)} AS
SELECT
  CAST(TRY_CAST(${txnDate} AS DATE) AS VARCHAR) AS txn_date,
  ${textDuckSql(transactionType)} AS transaction_type,
  ${textDuckSql(num)} AS num,
  ${textDuckSql(name)} AS name,
  ${textDuckSql(account)} AS account,
  ${textDuckSql(accountName)} AS account_name,
  ${textDuckSql(accountId)} AS account_id,
  ${accountNumberExpr} AS account_number,
  ${textDuckSql(customer)} AS customer,
  ${textDuckSql(customerId)} AS customer_id,
  ${textDuckSql(vendor)} AS vendor,
  ${textDuckSql(vendorId)} AS vendor_id,
  ${textDuckSql(employee)} AS employee,
  ${textDuckSql(employeeId)} AS employee_id,
  ${textDuckSql(klass)} AS class,
  ${textDuckSql(classId)} AS class_id,
  ${textDuckSql(department)} AS department,
  ${textDuckSql(department)} AS location,
  ${textDuckSql(departmentId)} AS department_id,
  ${textDuckSql(productService)} AS product_service,
  ${textDuckSql(productService)} AS item,
  ${textDuckSql(itemId)} AS item_id,
  ${textDuckSql(splitAccount)} AS split_account,
  ${textDuckSql(invoiceId)} AS invoice_id,
  ${textDuckSql(invoiceNum)} AS invoice_num,
  CAST(TRY_CAST(${invoiceDate} AS DATE) AS VARCHAR) AS invoice_date,
  CAST(TRY_CAST(${invoiceDueDate} AS DATE) AS VARCHAR) AS invoice_due_date,
  ${textDuckSql(salesTermId)} AS sales_term_id,
  ${textDuckSql(salesTermName)} AS sales_term_name,
  ${numericDuckSql(termDays)} AS term_days,
  ${textDuckSql(termsSource)} AS terms_source,
  ${textDuckSql(arPaid)} AS ar_paid,
  ${numericDuckSql(openBalance)} AS open_balance,
  ${textDuckSql(memo)} AS memo,
  ${numericDuckSql(quantity)} AS quantity,
  ${numericDuckSql(rate)} AS rate,
  ${textDuckSql(taxCode)} AS tax_code,
  ${numericDuckSql(taxAmount)} AS tax_amount,
  ${numericDuckSql(netAmount)} AS net_amount,
  ${numericDuckSql(debit)} AS debit,
  ${numericDuckSql(credit)} AS credit,
  ${numericDuckSql(amount)} AS amount,
  '' AS ${duckDbIdent(QBO_GL_DETAIL_SEARCH_COLUMN)},
  'QuickBooksGL' AS source,
  FALSE AS is_adjustment
FROM ${duckDbIdent(rawTableName)}
${accountJoin}
WHERE TRY_CAST(${txnDate} AS DATE) IS NOT NULL`
}

export function buildCanonicalGlDetailSearchTextSql(): string[] {
  const statements: string[] = []
  for (let i = 0; i < QBO_GL_DETAIL_SEARCH_TEXT_COLUMNS.length; i += QBO_GL_DETAIL_SEARCH_TEXT_BATCH_SIZE) {
    const batch = QBO_GL_DETAIL_SEARCH_TEXT_COLUMNS.slice(i, i + QBO_GL_DETAIL_SEARCH_TEXT_BATCH_SIZE)
    const parts = [
      `NULLIF(${duckDbIdent(QBO_GL_DETAIL_SEARCH_COLUMN)}, '')`,
      ...batch.map((column) => `NULLIF(${textDuckSql(duckDbIdent(column))}, '')`),
    ]
    statements.push(`
UPDATE ${duckDbIdent(QBO_GL_DETAIL_TABLE)}
SET ${duckDbIdent(QBO_GL_DETAIL_SEARCH_COLUMN)} = lower(trim(concat_ws(' ', ${parts.join(', ')})))
`)
  }
  return statements
}
